import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog } from 'radix-ui';
import { api } from '../services/api';
import { Product } from '../types';
import { Plus, Edit2, Trash2, X, Upload, Loader2, ListOrdered } from 'lucide-react';
import { ImageCropper } from '../components/ImageCropper';
import { CategoryOrderDialog } from '../components/CategoryOrderDialog';

export function AdminProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categoryOrder, setCategoryOrder] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showCategoryOrder, setShowCategoryOrder] = useState(false);

  const distinctCategories = useMemo(() => {
    const set = new Set<string>();
    products.forEach(p => { if (p.category) set.add(p.category); });
    return Array.from(set);
  }, [products]);

  const emptyForm = { name: '', description: '', price: '', taxRate: '', imageUrl: '', category: '', isActive: true };
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setError('');
    setCropFile(file);
  };

  const handleCropConfirm = async (blob: Blob) => {
    if (!cropFile) return;
    const ext = blob.type === 'image/png' ? 'png' : 'jpg';
    const baseName = (cropFile.name || 'image').replace(/\.[^.]+$/, '');
    const cropped = new File([blob], `${baseName}-square.${ext}`, { type: blob.type });
    setCropFile(null);
    setUploading(true);
    try {
      const { url } = await api.uploadProductImage(cropped);
      setForm(prev => ({ ...prev, imageUrl: url }));
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => { loadProducts(); }, []);

  // Pre-fill default tax rate from organization settings when opening a fresh form
  useEffect(() => {
    if (!showForm || editingProduct) return;
    api.getOrganizationSettings()
      .then((data: any) => {
        const settings = data?.settings || {};
        if (typeof settings.defaultTaxRate === 'number' && !form.taxRate) {
          setForm(prev => ({ ...prev, taxRate: String(settings.defaultTaxRate) }));
        }
      })
      .catch(() => {});
  }, [showForm, editingProduct]);

  const loadProducts = async () => {
    try {
      const { products, categoryOrder } = await api.getProducts(true);
      setProducts(products);
      setCategoryOrder(categoryOrder || []);
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const data = {
        name: form.name,
        description: form.description || undefined,
        price: Math.round(parseFloat(form.price) * 100),
        taxRate: parseFloat(form.taxRate) || 0,
        isActive: form.isActive,
        imageUrl: form.imageUrl || undefined,
        category: form.category || undefined,
      };
      if (editingProduct) await api.updateProduct(editingProduct.id, data as any);
      else await api.createProduct(data as any);
      closeForm();
      loadProducts();
    } catch (err: any) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const openAdd = () => {
    setEditingProduct(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      description: product.description || '',
      price: (product.price / 100).toFixed(2),
      taxRate: product.tax_rate.toString(),
      imageUrl: product.image_url || '',
      category: product.category || '',
      isActive: product.is_active,
    });
    setError('');
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this product?')) return;
    try { await api.deleteProduct(id); loadProducts(); }
    catch (err: any) { setError(err.message); }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setForm(emptyForm);
    setError('');
  };

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400 text-lg">Loading...</div>;

  const priceNum = parseFloat(form.price);
  const validPrice = !isNaN(priceNum) && priceNum > 0;

  return (
    <div className="max-w-5xl mx-auto pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 sticky top-0 bg-[#f0f4f8]/80 backdrop-blur-md py-4 z-10 border-b border-transparent">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Products</h1>
          <p className="text-slate-500 font-medium mt-1">{products.filter(p => p.is_active).length} active products</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCategoryOrder(true)}
            disabled={distinctCategories.length === 0}
            title={distinctCategories.length === 0 ? 'Add categories to products first' : 'Reorder how categories appear on the POS'}
            className="flex items-center gap-2 px-4 py-3.5 bg-white text-slate-700 border border-slate-200 rounded-2xl font-bold hover:bg-slate-50 hover:border-slate-300 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ListOrdered className="w-5 h-5" />
            <span className="hidden sm:inline">Reorder Categories</span>
          </button>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-5 py-3.5 bg-[#2563eb] text-white rounded-2xl font-bold hover:bg-[#1d4ed8] transition-all active:scale-95 shadow-md shadow-blue-900/10"
          >
            <Plus className="w-5 h-5" />
            Add Product
          </button>
        </div>
      </div>

      {error && !showForm && (
        <div className="px-5 py-4 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-8 font-medium">
          {error}
        </div>
      )}

      {/* Product list */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map(product => (
          <div key={product.id} className="bg-white p-5 rounded-[2rem] flex flex-col shadow-sm hover:shadow-md border border-slate-200/60 transition-all">
            <div className="flex items-start gap-4 mb-4">
              {/* Thumbnail */}
              <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 bg-slate-100 relative">
                {product.image_url
                  ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover absolute inset-0" onError={e => (e.currentTarget.style.display = 'none')} />
                  : <div className="absolute inset-0 flex items-center justify-center text-3xl font-bold text-[#2563eb] opacity-20 bg-slate-200/50">{product.name.charAt(0)}</div>
                }
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 pt-1">
                <h3 className="text-[17px] font-bold text-slate-800 leading-tight mb-1 truncate" title={product.name}>{product.name}</h3>
                <div className="flex flex-wrap gap-2 mb-2">
                  {product.category && <span className="text-[11px] px-2.5 py-0.5 bg-blue-50 text-[#1e40af] rounded-full font-bold uppercase tracking-wide">{product.category}</span>}
                  {product.tax_rate > 0 && <span className="text-[11px] px-2.5 py-0.5 bg-green-50 text-green-700 rounded-full font-bold uppercase tracking-wide">Taxable</span>}
                  {!product.is_active && <span className="text-[11px] px-2.5 py-0.5 bg-red-50 text-red-600 rounded-full font-bold uppercase tracking-wide">Inactive</span>}
                </div>
                <div className="text-xl font-extrabold text-[#2563eb]">
                  ${(product.price / 100).toFixed(2)}
                </div>
              </div>
            </div>

            {product.description && (
              <p className="text-[13px] font-medium text-slate-500 mb-4 line-clamp-2 leading-relaxed">
                {product.description}
              </p>
            )}

            {/* Actions */}
            <div className="mt-auto flex gap-2 pt-2">
              <button onClick={() => handleEdit(product)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[14px] font-bold text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all active:scale-95">
                <Edit2 className="w-4 h-4" /> Edit
              </button>
              <button onClick={() => handleDelete(product.id)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 border border-red-100 rounded-xl text-[14px] font-bold text-red-600 hover:bg-red-100 hover:text-red-700 transition-all active:scale-95">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {products.length === 0 && (
        <div className="bg-white rounded-[2rem] p-12 text-center border border-slate-200/60">
          <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center">
            <Plus className="w-8 h-8 text-[#2563eb]" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-1">No products yet</h3>
          <p className="text-slate-500 text-sm mb-6">Add your first product to start selling.</p>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#2563eb] text-white rounded-2xl font-bold hover:bg-[#1d4ed8] transition-all active:scale-95"
          >
            <Plus className="w-5 h-5" />
            Add Product
          </button>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      <Dialog.Root open={showForm} onOpenChange={(open) => { if (!open) closeForm(); else setShowForm(true); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-[2rem] shadow-2xl p-8 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
            <Dialog.Title className="text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </Dialog.Title>
            <Dialog.Description className="text-slate-500 font-medium mb-8">
              {editingProduct ? 'Update the product details below' : 'Create a new product following Stripe’s pricing model'}
            </Dialog.Description>

            {error && (
              <div className="px-4 py-3 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 text-sm font-medium">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              {/* Product Name */}
              <div>
                <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Iced Matcha Latte"
                  required
                  className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Price */}
                <div>
                  <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">
                    Price (USD) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-5 top-1/2 -translate-y-1/2 text-lg font-semibold text-slate-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.price}
                      onChange={e => setForm({ ...form, price: e.target.value })}
                      placeholder="0.00"
                      required
                      className="w-full h-14 pl-10 pr-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5 ml-1">
                    Stored as <span className="font-semibold text-slate-600">{validPrice ? `${Math.round(priceNum * 100)} cents` : '0 cents'}</span> in Stripe
                  </p>
                </div>

                {/* Tax Rate */}
                <div>
                  <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Tax Rate (%)</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.taxRate}
                      onChange={e => setForm({ ...form, taxRate: e.target.value })}
                      placeholder="e.g. 8.5"
                      className="w-full h-14 pl-5 pr-10 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
                    />
                    <span className="absolute right-5 top-1/2 -translate-y-1/2 text-lg font-semibold text-slate-400">%</span>
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Category</label>
                  <input
                    type="text"
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    placeholder="e.g. Coffee & Drinks"
                    className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
                  />
                </div>

                {/* Image */}
                <div>
                  <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">
                    Image <span className="text-slate-400 font-medium normal-case tracking-normal">(optional)</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={form.imageUrl}
                      onChange={e => setForm({ ...form, imageUrl: e.target.value })}
                      placeholder="Paste URL or upload →"
                      className="flex-1 min-w-0 h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleFileSelected}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center justify-center gap-2 h-14 px-4 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-2xl text-slate-700 font-bold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Upload from your computer"
                    >
                      {uploading
                        ? <Loader2 className="w-5 h-5 animate-spin" />
                        : <Upload className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5 ml-1">JPEG, PNG, WebP, GIF · max 5MB</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">
                  Description <span className="text-slate-400 font-medium normal-case tracking-normal">(optional)</span>
                </label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  placeholder="Short description shown on the product card"
                  className="w-full p-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300 resize-y"
                />
              </div>

              {/* Active toggle */}
              <div className="flex items-center justify-between p-5 bg-slate-50 rounded-2xl border border-slate-200">
                <div>
                  <p className="font-semibold text-slate-700">Active Product</p>
                  <p className="text-sm text-slate-500 mt-0.5">Visible to cashiers on the POS</p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, isActive: !prev.isActive }))}
                  aria-pressed={form.isActive}
                  className={`relative w-16 h-9 rounded-full transition-all flex-shrink-0 ${form.isActive ? 'bg-[#2563eb]' : 'bg-slate-300'}`}
                >
                  <span className={`absolute top-1 w-7 h-7 bg-white rounded-full shadow-sm transition-all ${form.isActive ? 'left-8' : 'left-1'}`} />
                </button>
              </div>

              {/* Image preview */}
              {form.imageUrl && (
                <div>
                  <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Preview</label>
                  <img src={form.imageUrl} alt="preview" className="w-32 h-32 object-cover rounded-2xl border-2 border-slate-100 shadow-sm" onError={e => (e.currentTarget.style.display = 'none')} />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={submitting}
                  className="flex-1 h-14 px-6 bg-slate-100 text-slate-600 rounded-2xl font-bold hover:bg-slate-200 transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`flex-1 h-14 px-6 rounded-2xl font-bold transition-all active:scale-[0.98] shadow-lg ${
                    submitting
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed shadow-none'
                      : 'bg-[#2563eb] text-white hover:bg-[#1d4ed8] shadow-blue-900/20'
                  }`}
                >
                  {submitting ? 'Saving…' : editingProduct ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>

            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="absolute top-6 right-6 w-10 h-10 rounded-full flex items-center justify-center hover:bg-slate-100 active:scale-95 transition-all"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ImageCropper
        file={cropFile}
        onCancel={() => setCropFile(null)}
        onConfirm={handleCropConfirm}
      />

      <CategoryOrderDialog
        open={showCategoryOrder}
        onClose={() => setShowCategoryOrder(false)}
        categories={distinctCategories}
        initialOrder={categoryOrder}
        onSaved={(order) => setCategoryOrder(order)}
      />
    </div>
  );
}
